import React, { useState } from "react";
import axios from "axios";
import { Phone, MessageCircle, Send, Sparkles, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { MODELS, recommendModel, telHref, telegramLink } from "../lib/config";
import { trackEvent, trackCall, captureUTM } from "../lib/analytics";

// Empty -> relative path (works on Cloudflare Pages with Functions on same origin)
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function Quiz({ lang, T }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ segment: "", size: "", city: "", name: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const segments = T.segments.items.map((s) => ({ key: s.key, label: s.title[lang] }));
  const sizes = T.quiz.sizes[lang];
  const cities = T.quiz.cities;

  const recId = data.segment && data.size ? recommendModel(data.segment, data.size) : null;
  const model = recId ? MODELS.find((m) => m.id === recId) : null;

  const total = 5;
  const progress = ((step + 1) / total) * 100;

  const next = () => {
    trackEvent("QuizStep", { step: step + 1, language: lang });
    setStep((s) => Math.min(s + 1, total - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!data.phone || data.phone.length < 6) return;
    setBusy(true);
    const payload = {
      ...data,
      recommended_model: recId,
      language: lang,
      source: "quiz",
      utm: captureUTM(),
    };
    try {
      if (BACKEND !== undefined) await axios.post(`${BACKEND}/api/leads`, payload);
      trackEvent("LeadFormSubmit", { source: "quiz", model: recId, language: lang });
      setSubmitted(true);
      setStep(total - 1);
    } catch (e) {
      trackEvent("LeadFormError", { language: lang });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="quiz" className="relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 reveal">
            <span className="chip mb-3">QUIZ · 1 MIN</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">{T.quiz.h2[lang]}</h2>
            <p className="mt-4 text-muted max-w-md">
              {lang === "uz"
                ? "4 ta savol — va biz mos modelni taklif qilamiz. Aniq tanlovni menejer telefonda tasdiqlaydi."
                : "4 шага — и мы предложим подходящую модель. Точный подбор подтвердит менеджер по звонку."}
            </p>
            <div className="hidden lg:block mt-6 relative rounded-[28px] overflow-hidden glass">
              <img src="/images/aircooler/7.png" alt="" loading="lazy" className="w-full h-72 object-cover" />
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="glass rounded-[28px] p-5 md:p-7" data-testid="quiz-card">
              {/* Progress */}
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs uppercase tracking-widest text-muted">
                  {lang === "uz" ? "Qadam" : "Шаг"} {Math.min(step + 1, total)} / {total}
                </div>
                <div className="text-xs text-muted">
                  {recId && step >= 1 ? <>→ <span className="text-ice-300 font-semibold">{recId}</span></> : null}
                </div>
              </div>
              <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mb-7">
                <div className="h-full bg-gradient-to-r from-ice-400 to-ice-300 transition-all" style={{ width: `${progress}%` }} />
              </div>

              {step === 0 && (
                <div>
                  <h3 className="font-display font-bold text-2xl mb-4">{T.quiz.steps.where[lang]}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {segments.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => { setData((d) => ({ ...d, segment: s.key })); next(); }}
                        data-testid={`quiz-segment-${s.key}`}
                        className={`rounded-2xl px-4 py-4 text-left border transition ${
                          data.segment === s.key
                            ? "border-ice-400 bg-ice-400/10 text-white"
                            : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/90"
                        }`}
                      >
                        <div className="text-sm font-semibold">{s.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <h3 className="font-display font-bold text-2xl mb-4">{T.quiz.steps.size[lang]}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {sizes.map((s) => (
                      <button
                        key={s.k}
                        onClick={() => { setData((d) => ({ ...d, size: s.k })); next(); }}
                        data-testid={`quiz-size-${s.k}`}
                        className={`rounded-2xl px-4 py-4 border transition ${
                          data.size === s.k
                            ? "border-ice-400 bg-ice-400/10 text-white"
                            : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/90"
                        }`}
                      >
                        <div className="text-sm font-semibold">{s.v}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h3 className="font-display font-bold text-2xl mb-4">{T.quiz.steps.city[lang]}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {cities.map((c) => (
                      <button
                        key={c}
                        onClick={() => { setData((d) => ({ ...d, city: c })); next(); }}
                        data-testid={`quiz-city-${c.split(" ")[0]}`}
                        className={`rounded-2xl px-4 py-3 border text-sm transition ${
                          data.city === c
                            ? "border-ice-400 bg-ice-400/10 text-white"
                            : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/90"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h3 className="font-display font-bold text-2xl mb-2">{T.quiz.steps.contact[lang]}</h3>
                  <p className="text-muted text-sm mb-5">
                    {lang === "uz" ? "Menejer 1–2 daqiqada bog'lanadi." : "Менеджер свяжется в течение 1–2 минут."}
                  </p>
                  <div className="space-y-3">
                    <input
                      data-testid="quiz-input-name"
                      value={data.name}
                      onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))}
                      placeholder={T.quiz.namePh[lang]}
                      className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-ice-400"
                    />
                    <input
                      data-testid="quiz-input-phone"
                      value={data.phone}
                      onChange={(e) => setData((d) => ({ ...d, phone: e.target.value }))}
                      placeholder={T.quiz.phonePh[lang]}
                      inputMode="tel"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-ice-400"
                    />
                    <button
                      onClick={submit}
                      disabled={busy || !data.phone}
                      data-testid="quiz-submit-button"
                      className="w-full inline-flex items-center justify-center gap-2 btn-call rounded-full px-6 py-4 text-lg shadow-flame disabled:opacity-60"
                    >
                      <Sparkles size={18} /> {busy ? "..." : T.quiz.submit[lang]}
                    </button>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="quiz-result">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 rounded-full grid place-items-center bg-ice-400/15 text-ice-300">
                      <Check size={18} />
                    </span>
                    <h3 className="font-display font-bold text-2xl">{T.quiz.steps.result[lang]}</h3>
                  </div>
                  {submitted && (
                    <p className="text-sm text-ice-300 mb-4">{T.quiz.submitted[lang]}</p>
                  )}
                  <div className="rounded-[20px] bg-ink-700/60 border border-white/10 p-5 flex flex-col sm:flex-row gap-5 items-center">
                    {model && (
                      <img src={model.image} alt="" className="w-28 h-28 object-cover rounded-2xl" />
                    )}
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-widest text-muted">{T.quiz.resultLine[lang]}</div>
                      <div className="font-display font-bold text-3xl">{recId || "Z-1250"}</div>
                      {model && <div className="mt-1 text-sm text-muted">{model.sizeHint[lang]} · ${model.price}</div>}
                      <p className="mt-2 text-sm text-white/80">{T.quiz.resultSub[lang]}</p>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href={telHref}
                      onClick={() => trackCall("quiz_result", { model: recId, language: lang })}
                      data-testid="quiz-result-call"
                      className="cta-pulse inline-flex items-center justify-center gap-2 btn-call rounded-full px-6 py-3.5 text-base shadow-flame"
                    >
                      <Phone size={18} strokeWidth={2.6} /> {T.cta.callConfirm[lang]}
                    </a>
                    <a
                      href={telegramLink}
                      target="_blank" rel="noreferrer"
                      onClick={() => trackEvent("ClickTelegram", { source: "quiz_result", language: lang })}
                      data-testid="quiz-result-telegram"
                      className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-base font-bold transition hover:brightness-110 shadow-lg"
                      style={{ background: "#229ED9", color: "white" }}
                    >
                      <Send size={18} /> {T.cta.writeTelegram[lang]}
                    </a>
                  </div>
                </div>
              )}

              {/* Nav */}
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={back}
                  disabled={step === 0}
                  data-testid="quiz-back"
                  className="inline-flex items-center gap-2 text-sm text-muted hover:text-white disabled:opacity-30 transition"
                >
                  <ChevronLeft size={16} /> {T.quiz.back[lang]}
                </button>
                {step < 3 && (
                  <button
                    onClick={next}
                    disabled={step === 0 ? !data.segment : step === 1 ? !data.size : !data.city}
                    data-testid="quiz-next"
                    className="inline-flex items-center gap-2 text-sm text-white bg-white/5 hover:bg-white/10 px-4 py-2 rounded-full disabled:opacity-40 transition"
                  >
                    {T.quiz.next[lang]} <ChevronRight size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
